import React, { useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { User } from "../shared/interfaces/User";
import DataTableSkeleton from "../shared/DataTableSkeleton";
import { DataTable, DataTableMultiSortMetaType, DataTableSortParams } from "primereact/datatable";
import { Column } from "primereact/column";
import { TextTemplate } from "../shared/Grid/BodyTemplates";
import { FilterMatchMode } from "primereact/api";
import { useTranslation } from "react-i18next";
import { LOCALSTORAGE_KEYS } from "../shared/constants";
import makeStyles from "@mui/styles/makeStyles";
import { UserActionsTemplate } from "./UserActionsTemplate";
import { UserListHeaderToolbar } from "./UsersTableHeaderToolbar";

const sortFieldStorageKey = LOCALSTORAGE_KEYS.userManagementSortField;
const sortOrderStorageKey = LOCALSTORAGE_KEYS.userManagementSortOrder;
const INITIAL_ROW_AMOUNT = 25;
const TEN_ROW_AMOUNT = 10;
const FIFTY_ROW_AMOUNT = 50;
const HUNDRED_ROW_AMOUNT = 100;

const useStyles = makeStyles(() => ({
  iconGreen: {
    color: "green",
  },
  iconRed: {
    color: "red",
  },
}));

interface Props {
  onAddUserClick?: () => void;
  onUserDetailClick: (user: User) => void;
  userList: User[];
  loading: boolean;
  onDataRefreshClick: () => void;
}

export function UserList(props: Props) {
  const { t } = useTranslation();
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [multiSortMeta, setMultiSortMeta] = useState<DataTableMultiSortMetaType>();

  const classes = useStyles();

  const handleSort = (e: DataTableSortParams) => {
    const field = e.multiSortMeta?.at(0)?.field as string;
    const order = e.multiSortMeta?.at(0)?.order;
    localStorage.setItem(sortFieldStorageKey, field);
    order && localStorage.setItem(sortOrderStorageKey, order?.toString());
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  };

  if (props.loading) return <DataTableSkeleton />;
  else
    return (
      <React.Fragment>
        <div className="datatable-responsive">
          <DataTable
            value={props.userList}
            paginator={true}
            rows={INITIAL_ROW_AMOUNT}
            header={
              <UserListHeaderToolbar
                onGlobalFilter={setGlobalFilter}
                onDataRefreshClick={props.onDataRefreshClick}
                loading={props.loading}
                onAddUserClick={props.onAddUserClick}
              />
            }
            // If the global filter is an empty string add a space, otherwise some results may get filtered out
            globalFilter={globalFilter === "" ? " " : globalFilter}
            filterDisplay="row"
            removableSort
            sortMode="multiple"
            className="p-datatable-responsive p-datatable-gridlines p-datatable-sm"
            emptyMessage={t("common.emptyMessage", {
              val: "$t(common.users )",
            })}
            loading={props.loading}
            multiSortMeta={multiSortMeta}
            onSort={handleSort}
            paginatorTemplate={
              "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            }
            rowsPerPageOptions={[TEN_ROW_AMOUNT, INITIAL_ROW_AMOUNT, FIFTY_ROW_AMOUNT, HUNDRED_ROW_AMOUNT]}
          >
            <Column
              field={"userName"}
              header={t("login.username")}
              body={(rowData: User) => (
                <TextTemplate rowData={rowData} field={"userName"} header={t("login.username")} />
              )}
              filter
              filterMatchMode={FilterMatchMode.CONTAINS}
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="userName"
            />
            <Column
              field={"email"}
              header={t("login.email")}
              body={(rowData: User) => <TextTemplate rowData={rowData} field={"email"} header={t("login.email")} />}
              filter
              sortable
              filterMatchMode={FilterMatchMode.CONTAINS}
              showFilterMenu={false}
              showClearButton={false}
              sortField="email"
            />
            <Column
              field={"emailConfirmed"}
              header={t("userManagement.emailConfirmed")}
              body={(rowData: User) =>
                rowData.emailConfirmed ? (
                  <CheckIcon className={classes.iconGreen} />
                ) : (
                  <CloseIcon className={classes.iconRed} />
                )
              }
              sortable
              showFilterMenu={false}
              showClearButton={false}
              sortField="emailConfirmed"
            />
            <Column
              field={"isActive"}
              header={t("userManagement.isActive")}
              body={(rowData: User) =>
                rowData.isActive ? (
                  <CheckIcon className={classes.iconGreen} />
                ) : (
                  <CloseIcon className={classes.iconRed} />
                )
              }
              sortable
              showFilterMenu={false}
              showClearButton={false}
              sortField="isActive"
            />
            <Column
              body={(rowData: User) => (
                <UserActionsTemplate rowData={rowData} onUserDetailClick={props.onUserDetailClick} />
              )}
              headerStyle={{ width: "8em", textAlign: "center" }}
              bodyStyle={{ textAlign: "center", overflow: "visible" }}
              header={t("common.actions")}
            />
          </DataTable>
        </div>
      </React.Fragment>
    );
}
